import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  Button,
  TextField,
  Divider,
  Tab,
  Tabs,
  IconButton,
  Switch,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Slider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { userAPI, authAPI } from "../../services/api";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../components/common/LanguageSwitcher";
import CustomContainer from "../../components/common/CustomContainer";

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
  </div>
);

const Profile = () => {
  const { t } = useTranslation("common");
  const { user, checkAuth } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { toggleTheme, isDarkMode } = useTheme();
  const { currentLanguage } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [settings, setSettings] = useState({
    theme: "light",
    preferred_language: currentLanguage || "en",
    reading_view_mode: "single",
    default_zoom_level: 1.0,
    auto_save_progress: true,
    show_page_numbers: true,
    session_timeout_minutes: 30,
  });

  const fetchUserProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      console.log("Profile response:", response);
      const userData = response.data.user;
      if (userData) {
        console.log("Setting form data with user data:", userData);
        setFormData((prev) => ({
          ...prev,
          firstName: userData.firstName || userData.first_name || "",
          lastName: userData.lastName || userData.last_name || "",
          email: userData.email || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      showSnackbar(t("profile.errors.profileFetch"), "error");
    }
  };

  const fetchUserSettings = async () => {
    try {
      const response = await userAPI.getSettings();
      if (response.data) {
        const numericSettings = {
          ...response.data,
          default_zoom_level:
            parseFloat(response.data.default_zoom_level) || 1.0,
          session_timeout_minutes:
            parseInt(response.data.session_timeout_minutes) || 30,
        };
        setSettings(numericSettings);

        // Apply theme immediately if it differs from current state
        if (numericSettings.theme) {
          const shouldBeDark = numericSettings.theme === "dark";
          if (shouldBeDark !== isDarkMode) {
            toggleTheme();
          }
        }

        // Language will be handled by LanguageProvider
      }
    } catch (error) {
      console.error("Error fetching user settings:", error);
      showSnackbar(t("profile.errors.loadSettings"), "error");
    }
  };

  // Update theme setting when isDarkMode changes
  useEffect(() => {
    setSettings((prev) => ({
      ...prev,
      theme: isDarkMode ? "dark" : "light",
    }));
  }, [isDarkMode]);

  useEffect(() => {
    // Always fetch profile data and settings on mount
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchUserProfile();
        await fetchUserSettings();
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []); // Empty dependency array for mount-only effect

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleInputChange = (e) => {
    const { name, value, checked } = e.target;
    const newValue = e.target.type === "checkbox" ? checked : value;

    if (name in formData) {
      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    } else {
      setSettings((prev) => ({
        ...prev,
        [name]: newValue,
      }));

      // Immediately apply theme changes
      if (name === "theme" && (!editMode || value !== settings.theme)) {
        const shouldBeDark = newValue === "dark";
        if (shouldBeDark !== isDarkMode) {
          toggleTheme();
        }
        // Update theme in backend
        userAPI
          .getSettings()
          .then((response) => {
            return userAPI.updateSettings({
              ...response.data,
              theme: newValue,
            });
          })
          .catch((error) => {
            console.error("Failed to update theme:", error);
            showSnackbar(t("profile.errors.updateFailed"), "error");
          });
      }
    }
  };

  const handleLanguageChange = (newLang) => {
    setSettings((prev) => ({
      ...prev,
      preferred_language: newLang,
    }));
  };

  const handleSliderChange = (name) => (e, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const errors = [];
    if (formData.firstName.trim() === "")
      errors.push(t("profile.validation.firstNameRequired"));
    if (formData.lastName.trim() === "")
      errors.push(t("profile.validation.lastNameRequired"));
    if (formData.email.trim() === "")
      errors.push(t("profile.validation.emailRequired"));

    if (formData.newPassword) {
      if (!formData.currentPassword)
        errors.push(t("profile.validation.currentPasswordRequired"));
      if (formData.newPassword.length < 6)
        errors.push(t("profile.validation.passwordLength"));
      if (formData.newPassword !== formData.confirmPassword)
        errors.push(t("profile.validation.passwordMatch"));
    }

    if (errors.length > 0) {
      showSnackbar(errors.join(". "), "error");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const profileData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      };

      if (formData.newPassword) {
        profileData.currentPassword = formData.currentPassword;
        profileData.newPassword = formData.newPassword;
      }

      const numericSettings = {
        ...settings,
        default_zoom_level: Number(settings.default_zoom_level),
        session_timeout_minutes: Number(settings.session_timeout_minutes),
      };

      await userAPI.updateProfile(profileData);

      if (!(await checkAuth())) {
        showSnackbar(t("profile.errors.refreshNeeded"), "warning");
        return;
      }

      // Get current settings and merge with updates
      const currentSettings = await userAPI.getSettings();
      await userAPI.updateSettings({
        ...currentSettings.data,
        ...numericSettings,
      });
      showSnackbar(t("profile.success.update"), "success");
      setEditMode(false);
    } catch (error) {
      console.error("Update failed:", error);

      let errorMessage = t("profile.errors.updateFailed");

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message === "Failed to refresh user data") {
        errorMessage = t("profile.errors.refreshFailed");
      } else if (!error.response && error.message) {
        errorMessage = error.message;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomContainer>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* Profile Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: "flex", alignItems: "center", gap: 3 }}>
            <Avatar sx={{ width: 120, height: 120, fontSize: 48 }}>
              {formData.firstName?.[0]?.toUpperCase() ||
                formData.lastName?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" gutterBottom>
                {formData.firstName} {formData.lastName}
              </Typography>
              <Typography color="textSecondary">
                {t("profile.memberSince")}{" "}
                {(user?.created_at || user?.createdAt) &&
                  format(
                    new Date(user?.created_at || user?.createdAt),
                    "MMM yyyy"
                  )}
              </Typography>
            </Box>
            <Button
              variant={editMode ? "contained" : "outlined"}
              startIcon={editMode ? <SaveIcon /> : <EditIcon />}
              onClick={(e) => (editMode ? handleSubmit(e) : setEditMode(true))}
            >
              {editMode ? t("common.save") : t("common.edit")}
            </Button>
          </Paper>
        </Grid>

        {/* Content */}
        <Grid item xs={12}>
          <Paper sx={{ width: "100%" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label={t("profile.tabs.account")} />
              <Tab label={t("profile.tabs.security")} />
            </Tabs>

            {/* Account & Settings Tab */}
            <TabPanel value={activeTab} index={0}>
              <Grid container spacing={3}>
                {/* Personal Info */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {t("profile.sections.personal")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("profile.fields.firstName")}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("profile.fields.lastName")}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("profile.fields.email")}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                </Grid>

                {/* Interface Settings */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {t("profile.sections.interface")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label={t("profile.fields.theme")}
                    name="theme"
                    value={settings.theme}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  >
                    <MenuItem value="light">
                      {t("profile.theme.light")}
                    </MenuItem>
                    <MenuItem value="dark">{t("profile.theme.dark")}</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box>
                    <LanguageSwitcher
                      label={t("profile.fields.language")}
                      value={settings.preferred_language}
                      onChange={handleLanguageChange}
                      disabled={!editMode}
                    />
                  </Box>
                </Grid>

                {/* Reading Settings */}
                {/* <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {t("profile.sections.reading")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    label={t("profile.fields.viewMode")}
                    name="reading_view_mode"
                    value={settings.reading_view_mode}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  >
                    <MenuItem value="single">
                      {t("profile.viewMode.single")}
                    </MenuItem>
                    <MenuItem value="double">
                      {t("profile.viewMode.double")}
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ px: 2 }}>
                    <Typography gutterBottom>
                      {t("profile.fields.zoom")}
                    </Typography>
                    <Slider
                      value={settings.default_zoom_level}
                      onChange={handleSliderChange("default_zoom_level")}
                      min={0.5}
                      max={2.5}
                      step={0.1}
                      marks={[
                        { value: 0.5, label: "50%" },
                        { value: 1, label: "100%" },
                        { value: 2.5, label: "250%" },
                      ]}
                      disabled={!editMode}
                    />
                  </Box>
                </Grid> */}

                {/* Progress Settings */}
                {/* <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    {t("profile.sections.progress")}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.auto_save_progress}
                        onChange={handleInputChange}
                        name="auto_save_progress"
                        disabled={!editMode}
                      />
                    }
                    label={t("profile.fields.autoSave")}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.show_page_numbers}
                        onChange={handleInputChange}
                        name="show_page_numbers"
                        disabled={!editMode}
                      />
                    }
                    label={t("profile.fields.showPageNumbers")}
                  />
                </Grid> */}
              </Grid>
            </TabPanel>

            {/* Security Tab */}
            <TabPanel value={activeTab} index={1}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {t("profile.sections.password")}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("profile.fields.currentPassword")}
                    name="currentPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("profile.fields.newPassword")}
                    name="newPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("profile.fields.confirmPassword")}
                    name="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    disabled={!editMode}
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <Box sx={{ px: 2 }}>
                    <Typography gutterBottom>Session Timeout</Typography>
                    <Slider
                      value={settings.session_timeout_minutes}
                      onChange={handleSliderChange("session_timeout_minutes")}
                      min={5}
                      max={120}
                      step={5}
                      marks={[
                        { value: 5, label: "5m" },
                        { value: 30, label: "30m" },
                        { value: 60, label: "1h" },
                        { value: 120, label: "2h" },
                      ]}
                      disabled={!editMode}
                    />
                  </Box>
                </Grid> */}
              </Grid>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>
    </CustomContainer>
  );
};

export default Profile;
